import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Clock, Hourglass, Info, Lock, Rocket, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { CardFields, emptyCard, type CardForm } from "@/components/billing/CardFields";
import { PayIcon } from "@/components/billing/PayIcon";
import {
  PAY_METHOD_LABELS,
  PaymentMethodSelector,
  type PayMethod,
} from "@/components/billing/PaymentMethodSelector";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import { useCardEntry } from "@/components/billing/useCardEntry";
import { useDirectDebit } from "@/components/billing/useDirectDebit";
import type { ApplePayRecurring } from "@/components/billing/useWalletButton";
import { useWalletPayment } from "@/components/billing/useWalletPayment";
import {
  Button,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  cx,
  Section,
  KeyValue,
  buttonClass,
} from "@/components/ui";
import { attemptFinished, attemptKey } from "@/lib/attemptKey";
import { portal } from "@/lib/api";
import { approvalNeededBy, approvePayment } from "@/lib/approval";
import { ApiError } from "@/lib/http";
import { periodLabel } from "@/lib/billing";
import { formatDate, formatMoney } from "@/lib/format";
import { methodDetail, payableForUpgrade, processorName, type CardErrors } from "@/lib/payments";
import { supportsApplePay, supportsGooglePay, type Wallet } from "@/lib/stripe";
import type { BillingPeriod, CheckoutResult, PaymentMethod, PlanKey } from "@/lib/types";
import { BackLink, PageTitle, SectionTitle } from "@/components/billing/parts";

const NEW_CARD = "new";
/** The longest the "Receipt issued" step waits for refreshed billing data before showing the receipt. */
export const RECEIPT_WAIT_MS = 3000;

/** Figma "Billing — Upgrade to Growth · review and pay (BIL-01)" with "Taking payment" on top. */
export function CheckoutPage() {
  const { orgId, can, permissionsLoading } = useOrganization();
  const [params] = useSearchParams();
  const plan = params.get("plan") as PlanKey | null;
  const period = (params.get("period") === "annual" ? "annual" : "monthly") as BillingPeriod;
  const preview = useQuery({
    queryKey: ["plan-preview", orgId, plan, period],
    queryFn: () => portal.previewPlanChange(orgId, plan!, period),
    enabled: Boolean(plan) && can("billing:manage"),
  });
  const methods = useQuery({
    queryKey: ["payment-methods", orgId],
    queryFn: () => portal.paymentMethods(orgId),
  });

  if (permissionsLoading) return <FullPageSpinner />;
  if (!plan || !can("billing:manage")) return <Navigate to="/billing/plans" replace />;
  if (preview.isPending || methods.isPending) return <FullPageSpinner />;
  if (preview.isError) return <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />;
  if (methods.isError) return <ErrorState error={methods.error} onRetry={() => void methods.refetch()} />;
  if (preview.data.timing !== "now" || !preview.data.due_now) return <Navigate to="/billing/plans" replace />;

  return (
    <Checkout
      preview={preview.data}
      methods={methods.data.payment_methods}
      provider={methods.data.provider}
      publishableKey={methods.data.publishable_key}
    />
  );
}

type Step = "card" | "charge" | "plan" | "receipt";
const STEPS: Step[] = ["card", "charge", "plan", "receipt"];

function Checkout({
  preview,
  methods,
  provider,
  publishableKey,
}: {
  preview: NonNullable<Awaited<ReturnType<typeof portal.previewPlanChange>>>;
  methods: PaymentMethod[];
  provider: string;
  publishableKey: string | null | undefined;
}) {
  const { orgId } = useOrganization();
  // Google Pay has its own tab here, so it stays out of the card form.
  const entry = useCardEntry(orgId, { googlePay: false });
  const user = useUser();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // A saved Apple Pay card is kept for renewals: paying here needs a card (the API refuses it too),
  // or a fresh approval in Apple Pay's sheet (the Apple Pay tab). A bank account can pay: the upgrade
  // then starts once the Direct Debit clears.
  const usable = methods.filter(payableForUpgrade);
  const [params] = useSearchParams();
  /** How a new payment method is given ("Use a new payment method"); back from Stripe's Direct
   * Debit page, its tab. */
  const [method, setMethod] = useState<PayMethod>(
    params.get("method") === "bacs_debit" ? "bacs_debit" : "card",
  );
  const directDebit = useDirectDebit(orgId);
  const setUp = directDebit.returned.state === "done" ? directDebit.returned.method : null;
  /** What the person picked; until they pick, the bank account just set up (they confirm the amount
   * below), else the default. */
  const [picked, setChoice] = useState<string | null>(null);
  const choice = picked ?? setUp?.id ?? usable.find((m) => m.is_default)?.id ?? usable[0]?.id ?? NEW_CARD;
  const chosen = usable.find((m) => m.id === choice) ?? (setUp?.id === choice ? setUp : undefined);
  const byDebit = chosen?.kind === "bacs_debit";
  const [card, setCard] = useState<CardForm>(emptyCard(user.display_name ?? ""));
  const [cardErrors, setCardErrors] = useState<CardErrors>({});
  const [error, setError] = useState<unknown>(null);
  const [progress, setProgress] = useState<{ step: Step; label: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set on unmount so a payment finishing after the person left doesn't navigate them back. Reset on
  // every mount: StrictMode mounts, unmounts and remounts in development, which otherwise left this
  // stuck at true and the checkout on "Receipt issued" forever.
  const cancelled = useRef(false);
  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  const { plan, billing_period: period, due_now: due, next_charge: next } = preview;
  const total = formatMoney(due!.total_cents, due!.currency);
  const processor = processorName(provider);

  /** The charge went through: the last steps, then the receipt. A Direct Debit is only on its way:
   * nothing has changed yet, so straight to the page that says so. */
  const paid = async (result: CheckoutResult, label: string) => {
    if (result.invoice.status === "pending") {
      await Promise.allSettled([
        qc.invalidateQueries({ queryKey: ["invoices", orgId] }),
        qc.invalidateQueries({ queryKey: ["payment-methods", orgId] }),
      ]);
      if (!cancelled.current) navigate(`/billing/checkout/success/${result.invoice.id}`, { replace: true });
      return;
    }
    setProgress({ step: "plan", label });
    await new Promise((r) => setTimeout(r, 450));
    setProgress({ step: "receipt", label });
    // The charge is done: refresh what it changed, but never let a slow refresh hold back the
    // receipt. Past RECEIPT_WAIT_MS go anyway, dropping the cached subscription so the success
    // page loads the new plan instead of showing the old one.
    const refreshed = Promise.all([
      qc.invalidateQueries({ queryKey: ["subscription", orgId] }),
      qc.invalidateQueries({ queryKey: ["overview", orgId] }),
      qc.invalidateQueries({ queryKey: ["invoices", orgId] }),
      qc.invalidateQueries({ queryKey: ["payment-methods", orgId] }),
    ]).then(
      () => true,
      () => false,
    );
    const inTime = await Promise.race([
      refreshed,
      new Promise<false>((r) => setTimeout(() => r(false), RECEIPT_WAIT_MS)),
    ]);
    if (!inTime) qc.removeQueries({ queryKey: ["subscription", orgId] });
    await new Promise((r) => setTimeout(r, 450));
    if (!cancelled.current) navigate(`/billing/checkout/success/${result.invoice.id}`, { replace: true });
  };

  /**
   * The customer's bank wants them to approve the payment: take them there, then finish the same payment (no second
   * charge). Resolves to `null` when it went through, else to what to show instead.
   */
  const approveWithBank = async (err: unknown, label: string): Promise<unknown> => {
    const approval = approvalNeededBy(err);
    if (!approval) return err;
    try {
      setProgress({ step: "charge", label: "your bank" });
      const attempt = await approvePayment(approval, orgId, publishableKey);
      if (!attempt.invoice_id) {
        throw new Error(
          "Your payment went through. We're still finishing your upgrade: check Billing in a minute.",
        );
      }
      const [invoice, subscription] = await Promise.all([
        portal.invoice(orgId, attempt.invoice_id),
        portal.subscription(orgId),
      ]);
      await paid({ account: subscription.account, invoice }, label);
      return null;
    } catch (approvalError) {
      return approvalError;
    }
  };

  /**
   * Charges the upgrade to a card on file, or to `added`: a card the person just typed in for this
   * payment, kept (as the default) only once the payment goes through.
   */
  const charge = async (methodId: string, label: string, added: PaymentMethod | null) => {
    setError(null);
    // A Direct Debit isn't authorised there and then: no "Taking payment" steps for it.
    if (!byDebit) setProgress({ step: "charge", label });
    setSubmitting(true);
    // One hidden key per upgrade being paid for (this plan, this period): sending it twice pays once.
    const scope = `${orgId}:${plan.key}:${period}`;
    try {
      const result = await portal.checkout(
        orgId,
        { plan: plan.key, billing_period: period, payment_method_id: methodId, new_card: added !== null },
        attemptKey("upgrade", scope),
      );
      attemptFinished("upgrade", scope);
      await paid(result, label);
    } catch (caught) {
      const err = await approveWithBank(caught, label);
      if (err === null) {
        attemptFinished("upgrade", scope); // approved at the bank and finished: it was paid
        return;
      }
      attemptFinished("upgrade", scope, err);
      // Declined, the new card is removed again. Kept after any other failure (it is on file now):
      // choose it, so trying again doesn't add it twice.
      if (added && !(err instanceof ApiError && err.reason === "PAYMENT_DECLINED")) setChoice(added.id);
      setProgress(null);
      setError(err);
      void qc.invalidateQueries({ queryKey: ["payment-methods", orgId] });
    } finally {
      setSubmitting(false);
    }
  };

  const pay = async () => {
    setError(null);
    if (choice !== NEW_CARD) {
      const label = chosen?.label ?? "your card";
      if (!byDebit) setProgress({ step: "card", label });
      return charge(choice, label, null);
    }
    if (method === "bacs_debit") {
      // To Stripe's page to set up the Direct Debit; back here to confirm paying with it. It becomes the
      // default (for renewals) only once the upgrade's debit clears, like a new card once paid.
      return directDebit.start({ makeDefault: false, params: { method: "bacs_debit" } });
    }
    const errors = await entry.validate(card);
    setCardErrors(errors);
    if (Object.keys(errors).length) {
      // The name field is ours (focus it directly); any other card problem is Stripe's own form, so
      // its element moves focus itself.
      if (!card.name.trim()) document.querySelector<HTMLElement>('[name="cardholder_name"]')?.focus();
      else if ("card" in errors) entry.focus();
      return;
    }
    setProgress({ step: "card", label: "your card" });
    let added: PaymentMethod;
    try {
      added = await entry.addCard(card, { makeDefault: false });
    } catch (err) {
      setProgress(null);
      setError(err);
      return;
    }
    await charge(added.id, added.label, added);
  };

  // A wallet's own button takes the place of "Pay and upgrade"; its tab says when it can't show here.
  // The wallet's sheet shows the amount and the person approves the payment itself there (Apple
  // Pay's terms require it); the portal then takes it and keeps the card as the default.
  const wallet: Wallet | null =
    choice === NEW_CARD && (method === "apple_pay" || method === "google_pay") ? method : null;
  const walletHere =
    provider === "stripe" && (wallet === "apple_pay" ? supportsApplePay() : supportsGooglePay());
  // The upgrade paid in the wallet's sheet: started and finished by the plan checkout's wallet endpoints.
  const upgradeCalls = {
    start: () => portal.startWalletCheckout(orgId, { plan: plan.key, billing_period: period }),
    finish: (reference: string) => portal.finishWalletCheckout(orgId, reference),
  };
  const onWalletPaid = (result: CheckoutResult) =>
    void paid(result, result.invoice.payment_method_label ?? PAY_METHOD_LABELS[wallet ?? "google_pay"]);
  const { slot: googleSlot, ...google } = useWalletPayment(orgId, "google_pay", {
    ...upgradeCalls,
    amountCents: due!.total_cents,
    recurring: null,
    onPaid: onWalletPaid,
  });
  const recurring = useMemo<ApplePayRecurring | null>(
    () =>
      next
        ? {
            label: `${plan.name} plan`,
            amountCents: next.total_cents,
            interval: period === "annual" ? "year" : "month",
            startsAt: next.date,
          }
        : null,
    [next, plan.name, period],
  );
  const { slot: appleSlot, ...apple } = useWalletPayment(orgId, "apple_pay", {
    ...upgradeCalls,
    amountCents: due!.total_cents,
    recurring,
    onPaid: onWalletPaid,
  });
  const walletPay = wallet === "apple_pay" ? apple : google;
  const walletButton = wallet !== null && walletHere && walletPay.status !== "unavailable";

  return (
    <div className="flex flex-col gap-6">
      <BackLink to="/billing/plans" />
      <PageTitle
        title={`Upgrade to ${plan.name}`}
        subtitle="Review the amount and the date before you are charged"
      />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <Section variant="compact">
            <div className="flex items-center gap-2.5">
              <SectionTitle>Payment details</SectionTitle>
              <span className="ml-auto text-xs text-[#766e6a]">
                {usable.length
                  ? `${usable.length} card${usable.length > 1 ? "s" : ""} on file`
                  : "No card on file yet"}
              </span>
            </div>
            <div className="h-3.5" />
            <ErrorAlert error={error} />
            <ErrorAlert
              error={
                directDebit.error ??
                (directDebit.returned.state === "error" ? directDebit.returned.error : null)
              }
            />
            {setUp && choice === setUp.id && (
              <p
                role="status"
                className="mb-3.5 rounded-[10px] bg-[#d9f0e6] px-3.5 py-3 text-sm text-[#13734d]"
              >
                Direct Debit set up for {setUp.label}. Check the amount, then press Pay to upgrade.
              </p>
            )}
            {wallet && <ErrorAlert error={walletPay.error} />}
            {wallet && walletPay.retry && (
              <Button
                unstyled
                type="button"
                onClick={walletPay.retry}
                disabled={walletPay.saving}
                aria-busy={walletPay.saving}
                className="mb-3.5 text-sm font-semibold text-brand-500 hover:underline disabled:opacity-60"
              >
                Try again
              </Button>
            )}
            {usable.length > 0 && (
              <div role="radiogroup" aria-label="Pay with" className="mb-3.5 flex flex-col gap-2">
                {[
                  ...usable.map((m) => ({ id: m.id, label: m.label, hint: methodDetail(m) })),
                  { id: NEW_CARD, label: "Use a new payment method", hint: "" },
                ].map((o) => (
                  <label
                    key={o.id}
                    className={cx(
                      "flex cursor-pointer items-center gap-3 rounded-[10px] border px-3.5 py-3 text-sm",
                      choice === o.id ? "border-[1.5px] border-brand-500 bg-brand-50" : "border-[#d4cac4]",
                    )}
                  >
                    <input
                      type="radio"
                      name="pay-with"
                      className="accent-brand-500"
                      checked={choice === o.id}
                      onChange={() => setChoice(o.id)}
                    />
                    <span className="font-medium text-fg">{o.label}</span>
                    {o.hint && <span className="ml-auto text-xs text-[#766e6a]">{o.hint}</span>}
                  </label>
                ))}
              </div>
            )}
            {choice === NEW_CARD && (
              <>
                <PaymentMethodSelector value={method} onChange={setMethod} methods={CHECKOUT_METHODS} />
                <div className="h-5" />
                {method === "bacs_debit" ? (
                  <BankDebitNote
                    total={total}
                    planName={plan.name}
                    connected={provider !== "none"}
                    cancelled={directDebit.returned.state === "cancelled"}
                  />
                ) : wallet ? (
                  <WalletNote
                    wallet={wallet}
                    total={total}
                    connected={provider === "stripe"}
                    here={walletHere}
                    unavailable={walletPay.status === "unavailable"}
                  />
                ) : (
                  <CardFields entry={entry} value={card} onChange={setCard} errors={cardErrors} />
                )}
              </>
            )}
            <div className="h-3.5" />
            <p className="flex items-start gap-2.5 rounded-[10px] bg-[#faf6ef] px-3.5 py-3 text-xs text-fg-muted">
              <Lock className="mt-px size-[15px] shrink-0" aria-hidden />
              {byDebit || (choice === NEW_CARD && method === "bacs_debit")
                ? `${plan.name} starts once the Direct Debit clears, within 7 working days; until then you stay on your current plan. The account is then debited for renewals.`
                : "This card is stored for your subscription and any future domain or mailbox charges. You can change it at any time in Billing."}
            </p>
          </Section>

          <Section variant="compact">
            <SectionTitle>What you get straight away</SectionTitle>
            <div className="h-2.5" />
            {plan.features
              .filter((f) => f.included)
              .map((f) => (
                <p key={f.label} className="flex items-start gap-2.5 py-2 text-sm text-fg-muted">
                  <Check className="mt-0.5 size-[15px] shrink-0" aria-hidden />
                  {f.label}
                </p>
              ))}
          </Section>
        </div>

        <div className="flex w-full shrink-0 flex-col gap-4 lg:w-[380px]">
          <Section variant="compact">
            <SectionTitle size="md">Order summary</SectionTitle>
            <div className="h-3" />
            <KeyValue label={`${plan.name} · ${periodLabel(period)}`}>
              {formatMoney(due!.amount_cents, due!.currency)}
            </KeyValue>
            <KeyValue label={`VAT (${Math.round((due!.vat_cents / Math.max(due!.amount_cents, 1)) * 100)}%)`}>
              {formatMoney(due!.vat_cents, due!.currency)}
            </KeyValue>
            <KeyValue label="Today">{formatDate(due!.date)}</KeyValue>
            {next && <KeyValue label="Renews">{formatDate(next.date)}</KeyValue>}
            <div className="flex items-center gap-2.5 border-t border-border pt-3 pb-1">
              <span className="text-sm font-semibold text-fg">Charged today</span>
              <span className="ml-auto text-xl leading-7 font-semibold text-fg">{total}</span>
            </div>
            <div className="h-3.5" />
            {/* Mounted while its tab is open, hidden once it says it can't show: unmounting would reload it. */}
            {wallet && walletHere && (
              <div key={wallet} className={cx("relative h-11", !walletButton && "hidden")}>
                {walletPay.status === "loading" && (
                  <div className="absolute inset-0 animate-pulse rounded-[10px] bg-muted" aria-hidden />
                )}
                <div
                  ref={wallet === "apple_pay" ? appleSlot : googleSlot}
                  className={cx((walletPay.saving || progress !== null) && "pointer-events-none opacity-60")}
                />
              </div>
            )}
            {!walletButton && (
              <Button
                variant="brand"
                size="block"
                loading={
                  progress !== null ||
                  submitting ||
                  directDebit.starting ||
                  directDebit.returned.state === "completing"
                }
                disabledReason={
                  wallet !== null
                    ? `${PAY_METHOD_LABELS[wallet]} isn't available in this browser. Choose another way to pay.`
                    : choice === NEW_CARD && method === "bacs_debit" && provider === "none"
                      ? "Bank debit isn't switched on yet. Choose another way to pay."
                      : undefined
                }
                onClick={() => void pay()}
              >
                <Rocket className="size-4" aria-hidden />{" "}
                {choice === NEW_CARD && method === "bacs_debit"
                  ? directDebit.starting
                    ? "Opening Stripe…"
                    : "Set up Direct Debit"
                  : byDebit
                    ? `Pay ${total} by Direct Debit`
                    : `Pay ${total} and upgrade`}
              </Button>
            )}
            <PoweredByStripe provider={provider} className="mx-auto mt-2.5" />
            <div className="h-2.5" />
            <Button
              unstyled
              type="button"
              className={buttonClass("secondary", "block")}
              onClick={() => navigate("/billing/plans")}
            >
              Back to plans
            </Button>
            <p className="mt-3 text-xs text-fg-subtle">
              Renews {period === "annual" ? "every year" : "monthly"} until you cancel. Cancelling keeps{" "}
              {plan.name} until the end of the period you paid for.
            </p>
          </Section>
          <Section variant="compact">
            <p className="flex items-center gap-2 text-sm font-semibold text-fg">
              <Info className="size-4" aria-hidden /> Domains are not included here
            </p>
            <p className="mt-2 text-xs text-fg-muted">
              {plan.name} includes the cost of connecting a domain, not the domain itself. Registering one is
              a separate charge with its own renewal date.
            </p>
          </Section>
        </div>
      </div>

      {progress ? (
        <TakingPayment
          step={progress.step}
          total={total}
          cardLabel={progress.label}
          planName={plan.name}
          processor={processor}
          onClose={() => setProgress(null)}
        />
      ) : (
        // Between approving in the wallet's sheet and the charge going through.
        wallet &&
        walletPay.saving && (
          <TakingPayment
            step="card"
            total={total}
            cardLabel={PAY_METHOD_LABELS[wallet]}
            planName={plan.name}
            processor={processor}
          />
        )
      )}
    </div>
  );
}

/** Bank debit takes days to clear: an upgrade paid by it starts once it has. */
const CHECKOUT_METHODS: PayMethod[] = ["card", "apple_pay", "google_pay", "bacs_debit"];

/** The Bank debit tab: what happens, before going to Stripe's page to set it up. */
function BankDebitNote({
  total,
  planName,
  connected,
  cancelled,
}: {
  total: string;
  planName: string;
  connected: boolean;
  /** Back from Stripe's page without finishing. */
  cancelled: boolean;
}) {
  return (
    <p role="status" className="flex items-start gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted">
      <PayIcon method="bacs_debit" className="mt-0.5 size-[18px]" />
      {!connected ? (
        <span>Bank debit isn't switched on yet. It appears here once payments are connected.</span>
      ) : (
        <span className="min-w-0">
          <span className="block">
            {cancelled
              ? "You left Stripe's page before finishing, so nothing was set up. Start again when you're ready."
              : `Set up a Direct Debit from a UK bank account on Stripe's secure page, then come back to confirm paying ${total}.`}
          </span>
          <span className="mt-1 block text-xs text-[#766e6a]">
            A Direct Debit takes up to 7 working days to clear. {planName} starts once it has; until then you
            stay on your current plan.
          </span>
        </span>
      )}
    </p>
  );
}

/** What each wallet's tab says on the checkout. */
const WALLET_COPY: Record<
  Wallet,
  { lead: (total: string) => string; detail: string; elsewhere: string; unavailable: string }
> = {
  apple_pay: {
    lead: (total) => `Press Apple Pay and approve ${total} with Face ID or Touch ID.`,
    detail:
      "Apple Pay is then kept as the default for your plan's renewals. Domains and other payments you make here use a card.",
    elsewhere:
      "Apple Pay works in Safari on an iPhone, iPad or Mac with a card in Apple Wallet. Open this page there, or pay by card instead.",
    unavailable: "Apple Pay isn't ready on this device. Add a card to Apple Wallet, or pay by card instead.",
  },
  google_pay: {
    lead: (total) => `Press Google Pay, choose a card saved in your Google account and approve ${total}.`,
    detail: "Once paid, it becomes your default card for renewals, domains and mailboxes.",
    elsewhere: "Google Pay needs a secure (https) page. Open this page over https, or pay by card instead.",
    unavailable: "Google Pay isn't available in this browser. Try Chrome or Edge, or pay by card instead.",
  },
};

/** A wallet's tab: what pressing its button does, or why it can't show here. */
function WalletNote({
  wallet,
  total,
  connected,
  here,
  unavailable,
}: {
  wallet: Wallet;
  total: string;
  /** Payments go through Stripe (wallets need it). */
  connected: boolean;
  /** This browser can show the wallet's button (Apple Pay: Safari on an Apple device; Google Pay: HTTPS). */
  here: boolean;
  /** The button loaded and said the wallet isn't available here. */
  unavailable: boolean;
}) {
  const copy = WALLET_COPY[wallet];
  return (
    <p role="status" className="flex items-start gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted">
      <PayIcon method={wallet} className="mt-0.5 size-[18px]" />
      {!connected ? (
        <span>
          {PAY_METHOD_LABELS[wallet]} isn't switched on yet. It appears here once Stripe is connected.
        </span>
      ) : !here ? (
        <span>{copy.elsewhere}</span>
      ) : unavailable ? (
        <span>{copy.unavailable}</span>
      ) : (
        <span className="min-w-0">
          <span className="block">{copy.lead(total)}</span>
          <span className="mt-1 block text-xs text-[#766e6a]">{copy.detail}</span>
        </span>
      )}
    </p>
  );
}

/** Figma "Modal / Taking payment": the charge's progress, step by step. */
function TakingPayment({
  step,
  total,
  cardLabel,
  planName,
  processor,
  onClose,
}: {
  step: Step;
  total: string;
  cardLabel: string;
  planName: string;
  processor: string;
  /** Closing is offered only before anything reaches the bank. */
  onClose?: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    }
  }, []);
  const at = STEPS.indexOf(step);
  const rows: [Step, string][] = [
    ["card", `Card details sent to ${processor}`],
    ["charge", "Charge authorised by your bank"],
    ["plan", `Plan updated to ${planName}`],
    ["receipt", "Receipt issued"],
  ];
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => e.preventDefault()}
      className="m-auto w-[calc(100%-2rem)] max-w-[480px] rounded-[20px] bg-surface p-0 text-fg shadow-[0_24px_60px_rgba(33,28,26,0.24)] backdrop:bg-[rgba(34,28,25,0.28)] backdrop:backdrop-blur-[8px]"
    >
      <div className="relative flex flex-col items-center px-8 pt-9 pb-7 text-center">
        {at === 0 && onClose && (
          <Button
            unstyled
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-6 right-6 rounded p-0.5 text-[#d63e1e] hover:bg-muted"
          >
            <X className="size-6" />
          </Button>
        )}
        <div className="flex size-[60px] items-center justify-center rounded-full bg-brand-50 text-[#d63e1e]">
          <Hourglass className="size-[26px] animate-pulse" aria-hidden />
        </div>
        <h2 id={titleId} className="mt-[18px] text-xl leading-7 font-semibold">
          Taking payment
        </h2>
        <p className="mt-1.5 max-w-[400px] text-sm text-fg-muted">
          Charging {total} to {cardLabel}. Keep this window open, your bank may ask you to approve it.
        </p>
        <ol className="mt-[22px] w-full rounded-xl bg-[#faf6ef] px-4 py-1.5 text-left">
          {rows.map(([key, label], i) => {
            const state = i < at ? "done" : i === at ? "active" : "waiting";
            return (
              <li key={key} className="flex items-center gap-2.5 py-[9px] text-xs">
                {state === "done" ? (
                  <CheckCircle2 className="size-[15px] text-fg-muted" aria-hidden />
                ) : state === "active" ? (
                  <Hourglass className="size-[15px] animate-pulse text-[#d63e1e]" aria-hidden />
                ) : (
                  <Clock className="size-[15px] text-fg-subtle" aria-hidden />
                )}
                <span className={state === "waiting" ? "text-fg-subtle" : "text-fg-muted"}>{label}</span>
                <span
                  className={cx(
                    "ml-auto font-semibold",
                    state === "done"
                      ? "text-[#766e6a]"
                      : state === "active"
                        ? "text-brand-700"
                        : "text-[#b3a8a3]",
                  )}
                >
                  {state === "done" ? "Done" : state === "active" ? "In progress" : "Waiting"}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="mt-5 flex items-center gap-2 text-xs text-fg-subtle">
          <Info className="size-3.5" aria-hidden /> Your plan does not change until the charge is confirmed
        </p>
        <p className="mt-2.5 flex items-center gap-2 text-xs text-fg-subtle">
          <Lock className="size-[13px]" aria-hidden /> Processed by {processor}
        </p>
      </div>
    </dialog>
  );
}
